import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from './Layout';
import Pager from '../components/Pager';
import StatusInfo from '../components/StatusInfo';
import { referralLabel, REFERRAL_STATUS_OPTIONS, REFERRAL_STATUS_DEFS } from '../utils/referralStatus';
import { fmtMoney } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const LIMIT = 20;

export default function Referrals() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('bound_at');
  const [order, setOrder] = useState('desc');
  const headers = { Authorization: 'Bearer ' + localStorage.getItem('clinicToken') };

  const fetchData = useCallback(() => {
    const params = { search: search || undefined, status: status || undefined, sort, order, limit: LIMIT, offset };
    axios.get(`${API}/clinic-cabinet/referrals`, { headers, params })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); }).catch(() => {});
    // eslint-disable-next-line
  }, [search, status, sort, order, offset]);

  useEffect(() => { const t = setTimeout(fetchData, 250); return () => clearTimeout(t); }, [fetchData]);

  const toggleSort = (f) => {
    setOffset(0);
    if (sort === f) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSort(f); setOrder('asc'); }
  };
  const arrow = (f) => (sort !== f ? '' : order === 'asc' ? ' ▲' : ' ▼');

  return (
    <Layout>
      <h1 style={s.h1}>Направления к вам ({total})</h1>
      <div style={s.filterBar}>
        <input style={s.input} placeholder="Поиск по пациенту или врачу" value={search}
          onChange={e => { setSearch(e.target.value); setOffset(0); }} />
        <select style={s.select} value={status} onChange={e => { setStatus(e.target.value); setOffset(0); }}>
          {REFERRAL_STATUS_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div style={s.table}>
        <div style={{ ...s.head, gridTemplateColumns: cols }}>
          <div>ID</div>
          <div style={s.thClickable} onClick={() => toggleSort('patient_name')}>Пациент{arrow('patient_name')}</div>
          <div style={s.thClickable} onClick={() => toggleSort('doctor_name')}>Направил врач{arrow('doctor_name')}</div>
          <div>ID врача</div>
          <div style={s.thClickable} onClick={() => toggleSort('status')}>
            Статус{arrow('status')}
            <span onClick={e => e.stopPropagation()}><StatusInfo title="Статусы направления" items={REFERRAL_STATUS_DEFS} /></span>
          </div>
          <div style={s.thClickable} onClick={() => toggleSort('bound_at')}>Привязан{arrow('bound_at')}</div>
          <div style={s.thClickable} onClick={() => toggleSort('paid_total')}>Оплачено{arrow('paid_total')}</div>
        </div>
        {items.map(r => (
          <div key={r.id} style={{ ...s.row, gridTemplateColumns: cols }}>
            <div style={s.refId} title="ID направления — продиктуйте его в техподдержку по спорному случаю">REF-{r.id}</div>
            <div style={s.strong}>{r.patient_name}</div>
            <div>{r.doctor_name}</div>
            <div style={s.refId}>{r.doctor_public_id || '—'}</div>
            <div><span style={{ ...s.badge, ...(r.status === 'primary_done' ? s.badgeTeal : s.badgeGray) }}>{referralLabel(r.status, r.clinic_confirmed_at)}</span></div>
            <div style={s.muted}>{new Date(r.bound_at).toLocaleDateString('ru')}</div>
            <div style={s.money}>{fmtMoney(r.paid_total)} ₽</div>
          </div>
        ))}
        {items.length === 0 && <div style={s.empty}>{total === 0 ? 'Направлений пока нет' : 'Ничего не найдено'}</div>}
      </div>
      <Pager total={total} limit={LIMIT} offset={offset} onPage={setOffset} />
    </Layout>
  );
}

const cols = '0.9fr 2fr 1.3fr 1.1fr 1.4fr 1fr 1fr';
const s = {
  h1: { color: '#1B2B4B', fontSize: 24, fontWeight: 700, marginBottom: 20 },
  filterBar: { display: 'flex', gap: 8, marginBottom: 16 },
  input: { flex: 1, maxWidth: 360, background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 8, padding: '8px 12px', fontSize: 14, outline: 'none' },
  select: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 8, padding: '8px 12px', fontSize: 14, outline: 'none' },
  table: { background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 16px rgba(27,43,75,0.05)' },
  head: { display: 'grid', padding: '14px 20px', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #eef2f7' },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  row: { display: 'grid', padding: '14px 20px', borderBottom: '1px solid #f4f7fb', fontSize: 14, color: '#1B2B4B', alignItems: 'center' },
  strong: { fontWeight: 600 },
  refId: { fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#94a3b8' },
  muted: { color: '#94a3b8', fontSize: 13 },
  money: { color: '#149e90', fontWeight: 600 },
  badge: { padding: '3px 10px', borderRadius: 20, fontSize: 12 },
  badgeTeal: { background: '#e9faf7', color: '#149e90' },
  badgeGray: { background: '#f1f5f9', color: '#64748b' },
  empty: { color: '#94a3b8', textAlign: 'center', padding: '40px 0' },
};
